import { NgZone } from "@angular/core";
import { BaseHost } from "./base-host";
import * as cmmn from '@utils/index';
import { logger } from "@services/logger/logger.service";
import { IDocumentChangeHandler, IOfficeHost } from "@core/shared/office-hosts/office-host.types";

const CUSTOM_ERROR_CODE = -1;

export interface IOutlookSelectionSnapshot
{
    readonly revision: number;
    readonly hasItem: boolean;
    readonly itemId: string | null;
    readonly internetMessageId: string | null;
    readonly conversationId: string | null;
    readonly isCompose?: boolean;
    readonly inReplyTo?: string | null;
}

export type IOutlookHeaderReadResult =
    | { readonly status: 'success'; readonly headers: string }
    | { readonly status: 'unsupported' | 'failed' | 'timeout' | 'stale' | 'unavailable' };

export class OutlookHost extends BaseHost implements IOfficeHost
{
    public static readonly NATIVE_READ_TIMEOUT_MS = 5_000;

    // @ts-ignore
    private item: Office.MessageCompose & Office.MessageRead;
    private isItemChanged: boolean = false;
    private selectionRevision = 0;
    private readonly documentChangeHandlers = new Set<IDocumentChangeHandler>();
    private readonly selectionItems = new WeakMap<IOutlookSelectionSnapshot, (Office.MessageCompose & Office.MessageRead) | null>();

    constructor(private readonly zone: NgZone)
    {
        super();
        Office.onReady((info) =>
        {
            if (info.host !== Office.HostType.Outlook)
            {
                throw new Error(`invalid host type: ${ info.host }`);
            }

            if (!Office.context.mailbox)
            {
                logger.log('Run inside Outlook to be able to use it.');
                return;
            }

            this.item = Office.context.mailbox.item!;
            this.selectionRevision += 1;

            Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, (event: any) =>
            {
                if (event.type == Office.EventType.ItemChanged)
                {
                    this.zone.run(() => this.onItemChanged());
                }
            }, { }, (result: Office.AsyncResult<void>) =>
            {
                if (result.status == Office.AsyncResultStatus.Failed)
                {
                    logger.error("addHandlerAsync(ItemChanged) error: " + JSON.stringify(result.error));
                }
            });
        });
    }

    public override async onDocumentChange(handler: IDocumentChangeHandler): Promise<void>
    {
        this.documentChangeHandlers.add(handler);
    }

    public offDocumentChange(handler: IDocumentChangeHandler): void
    {
        this.documentChangeHandlers.delete(handler);
    }

    private onItemChanged(): void
    {
        this.item = Office.context.mailbox.item!;
        this.selectionRevision += 1;
        this.isItemChanged = true;

        Array.from(this.documentChangeHandlers).forEach((handler) =>
        {
            try
            {
                void Promise.resolve(handler()).catch(() => logger.error('Outlook document-change handler failed'));
            }
            catch
            {
                logger.error('Outlook document-change handler failed');
            }
        });
    }

    public captureSelection(): IOutlookSelectionSnapshot
    {
        const subject = this.item?.subject as Office.Subject | string | undefined;
        const isCompose = !!subject && typeof subject === 'object' && typeof subject.getAsync === 'function';
        const snapshot: IOutlookSelectionSnapshot = Object.freeze({
            revision: this.selectionRevision,
            hasItem: Boolean(this.item),
            itemId: this.item?.itemId || null,
            internetMessageId: this.item?.internetMessageId || null,
            conversationId: this.item?.conversationId || null,
            isCompose,
            inReplyTo: isCompose ? this.item.inReplyTo || null : null,
        });
        this.selectionItems.set(snapshot, this.item);
        return snapshot;
    }

    public isSelectionCurrent(snapshot: IOutlookSelectionSnapshot): boolean
    {
        return this.selectionItems.has(snapshot)
            && snapshot.revision === this.selectionRevision
            && this.selectionItems.get(snapshot) === this.item;
    }

    public async getComposeTypeAsync(snapshot: IOutlookSelectionSnapshot): Promise<'reply' | 'other' | null>
    {
        if (!snapshot.isCompose || !this.isSelectionCurrent(snapshot))
        {
            return null;
        }

        const item = this.selectionItems.get(snapshot);
        if (!item || typeof item.getComposeTypeAsync !== 'function')
        {
            return null;
        }

        return new Promise((resolve) =>
        {
            const finish = (composeType: 'reply' | 'other' | null): void =>
            {
                clearTimeout(timeout);
                resolve(this.isSelectionCurrent(snapshot) ? composeType : null);
            };
            const timeout = setTimeout(() => finish(null), OutlookHost.NATIVE_READ_TIMEOUT_MS);

            try
            {
                item.getComposeTypeAsync((result) =>
                {
                    const value = result.value?.composeType;
                    finish(result.status === Office.AsyncResultStatus.Succeeded
                        && (value === 'reply' || value === 'forward' || value === 'newMail')
                        ? value === 'reply' ? 'reply' : 'other'
                        : null);
                });
            }
            catch
            {
                finish(null);
            }
        });
    }

    public async readInternetHeadersAsync(snapshot: IOutlookSelectionSnapshot): Promise<IOutlookHeaderReadResult>
    {
        if (!this.isSelectionCurrent(snapshot))
        {
            return { status: 'stale' };
        }

        const item = this.selectionItems.get(snapshot);
        if (!item)
        {
            return { status: 'unavailable' };
        }
        if (typeof item.getAllInternetHeadersAsync !== 'function')
        {
            return { status: 'unsupported' };
        }

        return new Promise((resolve) =>
        {
            const finish = (result: IOutlookHeaderReadResult): void =>
            {
                clearTimeout(timeout);
                resolve(this.isSelectionCurrent(snapshot) ? result : { status: 'stale' });
            };
            const timeout = setTimeout(() => finish({ status: 'timeout' }), OutlookHost.NATIVE_READ_TIMEOUT_MS);

            try
            {
                item.getAllInternetHeadersAsync((result) =>
                {
                    finish(result.status === Office.AsyncResultStatus.Succeeded && typeof result.value === 'string'
                        ? { status: 'success', headers: result.value }
                        : { status: 'failed' });
                });
            }
            catch
            {
                finish({ status: 'failed' });
            }
        });
    }

    public async getConversationIdAsync(): Promise<string | null>
    {
        if (!this.item)
        {
            return null;
        }

        return this.item.conversationId ?? null;
    }

    // Unlike conversationId (computed per-mailbox by Exchange, only guaranteed
    // equal across recipients within the same Exchange Online tenant), the
    // internetMessageId is the RFC 5322 Message-ID header set once by the
    // sending server and preserved verbatim in every recipient's copy - it
    // matches across personal/consumer accounts and different tenants too.
    public async getInternetMessageIdAsync(): Promise<string | null>
    {
        if (!this.item)
        {
            return null;
        }

        return this.item.internetMessageId ?? null;
    }

    public override async getDocumentIDAsync(): Promise<string>
    {
        if (!this.item)
        {
            throw new Error('Item not found');
        }

        let isSuccess = false;
        let messageId;

        if (!isSuccess)
        {
            messageId = this.item.itemId;
            if (messageId) isSuccess = true;
        }

        if (!isSuccess && this.item.getItemIdAsync)
        {
            isSuccess = await cmmn.callAsync<boolean>(async (r) =>
            {
                this.item.getItemIdAsync((idResult: Office.AsyncResult<string>) =>
                {
                    if (idResult.status == Office.AsyncResultStatus.Failed)
                    {
                        return r.next(false);
                    }

                    messageId = idResult.value;
                    return r.next(true);
                });
            });
        }

        if (!isSuccess && this.item.saveAsync)
        {
            isSuccess = await cmmn.callAsync<boolean>(async (r) =>
            {
                this.item.saveAsync((saveResult: Office.AsyncResult<string>) =>
                {
                    if (saveResult.status == Office.AsyncResultStatus.Failed)
                    {
                        return r.next(false);
                    }

                    messageId = saveResult.value;
                    return r.next(true);
                });
            });
        }

        if (!isSuccess)
        {
            messageId = this.item.internetMessageId;
            if (messageId) isSuccess = true;
        }

        if (isSuccess)
        {
            // @ts-ignore
            return messageId;
        };

        throw new Error(`[getDocumentIDAsync]: ${ isSuccess }`);
    }

    async setMessageComposeSelectedText(text: string,
        success: () => void,
        failed: (error: Office.Error) => void,
        html?: string,
    ): Promise<boolean>
    {
        this.isItemChanged = false;

        return await cmmn.callAsync<boolean>(async (r) =>
        {
            if (!this.item.body.getTypeAsync)
            {
                failed({
                    code: CUSTOM_ERROR_CODE,
                    message: `the message being compose must contain the 'getTypeAsync' function`,
                    name: 'Invalid MessageCompose item'
                });
                return r.next(false);
            }

            this.item.body.getTypeAsync((typeResult: Office.AsyncResult<Office.CoercionType>) =>
            {
                if (this.isItemChanged)
                {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `the item was changed before the selected text was set`,
                        name: 'ItemChanged event raized'
                    });
                    return r.next(false);
                }

                if (typeResult.status == Office.AsyncResultStatus.Failed)
                {
                    failed(typeResult.error);
                    return r.next(false);
                }

                if (!this.item.setSelectedDataAsync)
                {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `the message being compose must contain the 'setSelectedDataAsync' function`,
                        name: 'Invalid MessageCompose item'
                    });
                    return r.next(false);
                }

                let newData = text;
                if (typeResult.value == Office.CoercionType.Html)
                {
                    newData = html ?? text.replaceAll('\n', '<br />');
                }

                this.item.setSelectedDataAsync(newData, { coercionType: typeResult.value }, (selectedResult: Office.AsyncResult<any>) =>
                {
                    if (this.isItemChanged)
                    {
                        failed({
                            code: CUSTOM_ERROR_CODE,
                            message: `the item was changed before the selected text was set`,
                            name: 'ItemChanged event raized'
                        });
                        return r.next(false);
                    }

                    if (selectedResult.status === Office.AsyncResultStatus.Failed)
                    {
                        failed(selectedResult.error);
                        return r.next(false);
                    }

                    success();
                    return r.next(true);
                });
            });
        });
    }

    async getMessageComposeSelectedText(
        success: (result: string) => void,
        failed: (error: Office.Error) => void
    ): Promise<boolean>
    {
        this.isItemChanged = false;

        return await cmmn.callAsync<boolean>(async (r) =>
        {
            if (!this.item.body.getTypeAsync)
            {
                failed({
                    code: CUSTOM_ERROR_CODE,
                    message: `the message being compose must contain the 'getTypeAsync' function`,
                    name: 'Invalid MessageCompose item'
                });
                return r.next(false);
            }

            this.item.body.getTypeAsync((typeResult: Office.AsyncResult<Office.CoercionType>) =>
            {
                if (this.isItemChanged)
                {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `ItemChanged event raized: the item was changed before the type was defined`,
                        name: 'GetSelectedText failed'
                    });
                    return r.next(false);
                }

                if (typeResult.status == Office.AsyncResultStatus.Failed)
                {
                    failed(typeResult.error);
                    return r.next(false);
                }

                if (!this.item.getSelectedDataAsync)
                {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `the message being compose must contain the 'getSelectedDataAsync' function`,
                        name: 'GetSelectedText failed'
                    });
                    return r.next(false);
                }

                this.item.getSelectedDataAsync(Office.CoercionType.Text, (selectedResult: Office.AsyncResult<any>) =>
                {
                    if (this.isItemChanged)
                    {
                        failed({
                            code: CUSTOM_ERROR_CODE,
                            message: `ItemChanged event raized: the item was changed before the selected text was read`,
                            name: 'GetSelectedText failed'
                        });
                        return r.next(false);
                    }

                    if (selectedResult.status === Office.AsyncResultStatus.Failed)
                    {
                        failed(selectedResult.error);
                        return r.next(false);
                    }

                    let selectedText;

                    switch (typeResult.value)
                    {
                        case Office.CoercionType.Html:
                            selectedText = <string>selectedResult.value.data;
                            logger.log("getMessageComposeSelectedText() success: html[" + selectedText?.length + "]");
                            break;

                        case Office.CoercionType.Text:
                            selectedText = <string>selectedResult.value.data;
                            logger.log("getMessageComposeSelectedText() success: text[" + selectedText?.length + "]");
                            break;

                        default:
                            failed({
                                code: CUSTOM_ERROR_CODE,
                                message: 'Invalid Type: selected type unknown: ' + typeResult.value,
                                name: 'GetSelectedText failed'
                            });
                            return r.next(false);
                    }

                    success(selectedText);
                    return r.next(true);
                });
            });
        });
    }

    public override checkSelectable(): boolean
    {
        /// TODO: need to check getSelectedEntities() function:
        /// https://learn.microsoft.com/en-us/javascript/api/outlook/office.messageread?view=outlook-js-preview#outlook-office-messageread-getselectedentities-member(1)
        return this.item?.body.getTypeAsync != undefined;
    }

    public override checkEditable(): boolean
    {
        return this.item?.displayReplyForm == undefined;
    }

    public override async getSelectedContentAsync(): Promise<string>
    {
        let selectedText = '';

        try
        {
            await this.getMessageComposeSelectedText(
                (result) => { selectedText = result; },
                (error) => { logger.error(error.message); }
            );
        }
        catch (err)
        {
            if (err instanceof Error)
            {
                logger.error(`getSelectedContentAsync() error: ${ err?.message ?? JSON.stringify(err) }`);
            } else
            {
                logger.error(err);
            }
        }

        return selectedText;
    }

    public override async setSelectedTextAsync(plainText: string): Promise<void>
    {
        try
        {
            await this.setMessageComposeSelectedText(
                plainText,
                () => { },
                (error) => logger.error(error.message)
            );
        }
        catch (err)
        {
            if (err instanceof Error)
            {
                logger.error(this, `setSelectedTextAsync() error: ${ err[ 'message' ] ?? JSON.stringify(err) }`);
            } else
            {
                logger.error(err);
            }
        }

        logger.log("setSelectedTextAsync() success: text[" + plainText.length + "]");
    }

    public override async setSelectedFormattedTextAsync(plainText: string, html: string): Promise<void>
    {
        let errorMessage = 'Failed to insert formatted text into Outlook';
        const success = await this.setMessageComposeSelectedText(
            plainText,
            () => { },
            (error) => { errorMessage = error.message; },
            html,
        );
        if (!success)
        {
            throw new Error(errorMessage);
        }
    }

    public override checkDocumentReadable(): boolean
    {
        return Boolean(this.item);
    }

    public override getTag(): string
    {
        return 'outlook';
    }

    public override async attachImageToEmail(base64Image: string, filename: string): Promise<void>
    {
        this.item.addFileAttachmentAsync(
            base64Image,
            filename,
            { isInline: false },
            (result) =>
            {
                if (result.status === Office.AsyncResultStatus.Succeeded)
                {
                    logger.log('Attachment added successfully');

                    this.item.getAttachmentsAsync(function (asyncResult)
                    {
                        if (asyncResult.status === Office.AsyncResultStatus.Succeeded)
                        {
                            const attachments = asyncResult.value;
                            logger.log(attachments);
                        } else
                        {
                            logger.error("Error retrieving attachments: " + asyncResult.error.message);
                        }
                    });
                } else
                {
                    logger.error('Error adding attachment: ' + result.error.message);
                }
            }
        );
    }

    async getMessageText(
        success: (result: string) => void,
        failed: (error: Office.Error) => void
    ): Promise<boolean>
    {
        return await cmmn.callAsync<boolean>(async (r) =>
        {
            if (!this.item.body.getAsync)
            {
                failed({
                    code: CUSTOM_ERROR_CODE,
                    message: `the message must contain the 'getAsync' function`,
                    name: 'GetMessageText failed'
                });
                return r.next(false);
            }

            this.item.body.getAsync(
                Office.CoercionType.Text,
                (result) =>
                {
                    if (result.status === Office.AsyncResultStatus.Failed)
                    {
                        failed(result.error);
                        return r.next(false);
                    }

                    success(<string>result.value);
                    return r.next(true);
                }
            );
        });
    }

    public override async getDocumentTextAsync(): Promise<string>
    {
        let bodyText = '';

        try
        {
            await this.getMessageText(
                (result) => { bodyText = result; },
                (error) => { logger.error(error.message); }
            );

            logger.log("getMailBodyAsync[" + bodyText?.length + "]");
        }
        catch (err)
        {
            if (err instanceof Error)
            {
                logger.error(this, `getMailBodyAsync() error: ${ err[ 'message' ] ?? JSON.stringify(err) }`);
            } else
            {
                logger.error(err);
            }
        }

        return bodyText;
    }

    public override async setSelectedImageAsync(fileName: string, b64Text: string): Promise<void>
    {
        await cmmn.callAsync<boolean>(async (r) =>
        {
            this.item.addFileAttachmentFromBase64Async(b64Text, fileName, { isInline: true }, (attachResult: Office.AsyncResult<string>) =>
            {
                if (this.isItemChanged)
                {
                    logger.error(`the item was changed while attachment creating`);
                    return r.next(false);
                }

                if (attachResult.status == Office.AsyncResultStatus.Failed)
                {
                    logger.error(attachResult.error.message);
                    return r.next(false);
                }

                const imgHtml = `<img src="cid:${ fileName }" />`;

                this.item.setSelectedDataAsync(imgHtml, { coercionType: Office.CoercionType.Html }, (selectResult: Office.AsyncResult<any>) =>
                {
                    if (this.isItemChanged)
                    {
                        logger.error(`the item was changed while image inserting`);
                        return r.next(false);
                    }

                    if (selectResult.status == Office.AsyncResultStatus.Failed)
                    {
                        this.item.body.getTypeAsync((typeResult: Office.AsyncResult<Office.CoercionType>) =>
                        {
                            if (typeResult.status === Office.AsyncResultStatus.Failed)
                            {
                                logger.error(typeResult.error.message);
                                return r.next(false);
                            }

                            if (typeResult.value !== Office.CoercionType.Html)
                            {
                                logger.error(selectResult.error.message);
                                return r.next(false);
                            }

                            this.item.body.getAsync(Office.CoercionType.Html, (bodyResult: Office.AsyncResult<string>) =>
                            {
                                if (bodyResult.status === Office.AsyncResultStatus.Failed)
                                {
                                    logger.error(bodyResult.error.message);
                                    return r.next(false);
                                }

                                const bodyHtml = bodyResult.value || '';
                                this.item.body.setAsync(
                                    `${ bodyHtml }${ imgHtml }`,
                                    { coercionType: Office.CoercionType.Html },
                                    (setResult: Office.AsyncResult<void>) =>
                                    {
                                        if (setResult.status === Office.AsyncResultStatus.Failed)
                                        {
                                            logger.error(setResult.error.message);
                                            return r.next(false);
                                        }

                                        return r.next(true);
                                    }
                                );
                            });
                        });

                        return;
                    }

                    return r.next(true);
                });
            });
        });
    }
}
